<script setup>
import { useDataStore } from '@/stores/DataStore.js';
import { inject, ref, computed } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { useLang } from '@/stores/lang.js';
import { useLocale } from "vuetify";
import { formatNumber, getCookie } from "@/HelperFunction.js";

const store = useDataStore();
const { t } = useLocale();
const localeLang = useLang();
const page = usePage().props.option;

const props = defineProps({
    item: {
        type: Object,
        required: true,
    },
    itemsPerRow: {
        type: Number,
        default: 1,
    },
});

const isTwoColumns = computed(() => props.itemsPerRow === 2);

const hoveredItemId = ref(null);
const isHeart = inject("isHeart");
const toggleHeart = inject("toggleHeart");

const allSizes = computed(() => {
    if (props.item.sizes && props.item.sizes.length > 0) {
        const rawSizes = typeof props.item.sizes === 'string' ? JSON.parse(props.item.sizes) : props.item.sizes;
        return rawSizes.map((s, idx) => ({
            id: 'json_' + props.item.id + '_' + idx,
            size: {
                name: {
                    ku: s.name?.ku || s.name || '',
                    ar: s.name?.ar || s.name || '',
                    en: s.name?.en || s.name || ''
                }
            },
            item_id: props.item.id,
            size_id: null,
            price: Number(s.price) || 0,
            cost: 0
        }));
    }
    return [{
        id: 'json_' + props.item.id + '_0',
        size: {
            name: {
                ku: '',
                ar: '',
                en: ''
            }
        },
        item_id: props.item.id,
        size_id: null,
        price: Number(props.item.price) || 0,
        cost: 0
    }];
});

const itemDescription = computed(() => {
    if (!props.item?.description) return '';
    if (typeof props.item.description === 'string') return props.item.description;
    return props.item.description[localeLang.myPrefix] || props.item.description.ku || props.item.description.en || props.item.description.ar || '';
});

// Flavors Handling
const itemFlavors = computed(() => {
    if (!props.item?.flavors) return [];
    try {
        const raw = typeof props.item.flavors === 'string' ? JSON.parse(props.item.flavors) : props.item.flavors;
        if (!Array.isArray(raw)) return [];
        return raw.map((f) => ({
            name: typeof f.name === 'object' ? (f.name[localeLang.myPrefix] || f.name.ku || f.name.ar || f.name.en || '') : f.name,
            price: Number(f.price) || 0,
        })).filter(f => !!f.name);
    } catch {
        return [];
    }
});

const maxFlavorsLimit = computed(() => {
    return Number(props.item?.max_flavors) || null;
});

const flavorDialog = ref(false);
const selectedSizeForFlavor = ref(null);
const flavorSelections = ref([]);

function openFlavorModal(size) {
    selectedSizeForFlavor.value = size;
    flavorSelections.value = itemFlavors.value.map((f) => ({
        name: f.name,
        price: f.price,
        qty: 0,
    }));
    flavorDialog.value = true;
}

const totalSelectedFlavorsCount = computed(() => {
    return flavorSelections.value.reduce((sum, f) => sum + f.qty, 0);
});

const canAddMoreFlavors = computed(() => {
    if (!maxFlavorsLimit.value) return true;
    return totalSelectedFlavorsCount.value < maxFlavorsLimit.value;
});

function incrementFlavor(f) {
    if (canAddMoreFlavors.value) {
        f.qty++;
    }
}

function decrementFlavor(f) {
    if (f.qty > 0) {
        f.qty--;
    }
}

const flavorExtraPriceTotal = computed(() => {
    return flavorSelections.value.reduce((sum, f) => sum + (f.price * f.qty), 0);
});

const currentModalTotalPrice = computed(() => {
    const base = Number(selectedSizeForFlavor.value?.price) || 0;
    if (flavorExtraPriceTotal.value > 0) {
        return flavorExtraPriceTotal.value;
    }
    return base;
});

const isFlavorRequirementMet = computed(() => {
    if (maxFlavorsLimit.value) {
        return totalSelectedFlavorsCount.value === maxFlavorsLimit.value;
    }
    return totalSelectedFlavorsCount.value > 0;
});

function confirmFlavorOrder() {
    if (selectedSizeForFlavor.value && isFlavorRequirementMet.value) {
        store.ToOrder(
            selectedSizeForFlavor.value,
            1,
            props.item.name,
            flavorSelections.value.filter((f) => f.qty > 0)
        );
        flavorDialog.value = false;
    }
}

function handleOrderClick(size) {
    if (itemFlavors.value.length > 0) {
        openFlavorModal(size);
    } else {
        store.ToOrder(size, 1, props.item.name);
    }
}

const getItemOrderCount = (sizeId) => {
    let count = 0;
    Object.entries(store.orders || {}).forEach(([key, order]) => {
        if (key === sizeId || key.startsWith(sizeId + '_f_')) {
            count += (order.qty || 0);
        }
    });
    return count;
};
</script>

<template>
    <!-- Flavors Selection Modal Dialog -->
    <v-dialog v-model="flavorDialog" max-width="480px" persistent>
        <v-card class="rounded-xl overflow-hidden bg-surface">
            <!-- Modal Header -->
            <v-card-item class="pb-3 border-b bg-surface">
                <div class="d-flex align-center justify-space-between">
                    <div class="d-flex align-center">
                        <v-avatar color="deep-purple-accent-1" size="40" class="me-3">
                            <v-icon color="deep-purple-darken-2" size="24">mdi-ice-cream</v-icon>
                        </v-avatar>
                        <div>
                            <h3 class="text-subtitle-1 font-weight-bold mb-0">
                                {{ item.name[localeLang.myPrefix] || item.name.ku }}
                            </h3>
                            <span v-if="selectedSizeForFlavor?.size?.name?.ku && selectedSizeForFlavor?.size?.name?.ku !== 'سادە'" class="text-caption text-medium-emphasis">
                                {{ selectedSizeForFlavor.size.name[localeLang.myPrefix] || selectedSizeForFlavor.size.name.ku }}
                            </span>
                        </div>
                    </div>
                    <v-btn icon="mdi-close" variant="text" size="small" @click="flavorDialog = false" :aria-label="t('$vuetify.close')"></v-btn>
                </div>
            </v-card-item>

            <!-- Modal Body (Flavors List) -->
            <v-card-text class="pa-4 overflow-y-auto" style="max-height: 60vh;">
                <!-- Limit Status Bar -->
                <div class="pa-3 rounded-lg mb-3 border d-flex align-center justify-space-between"
                    :class="isFlavorRequirementMet ? 'bg-green-lighten-5 border-green text-green-darken-4' : 'bg-purple-lighten-5 border-purple-lighten-2 text-purple-darken-4'">
                    <div class="d-flex align-center">
                        <v-icon :color="isFlavorRequirementMet ? 'green-darken-2' : 'deep-purple-accent-3'" size="20" class="me-2">
                            {{ isFlavorRequirementMet ? 'mdi-check-circle' : 'mdi-format-list-checks' }}
                        </v-icon>
                        <span class="text-caption font-weight-bold">
                            <template v-if="maxFlavorsLimit">
                                {{ t('$vuetify.scoops') }}: {{ totalSelectedFlavorsCount }} / {{ maxFlavorsLimit }}
                                <span v-if="!isFlavorRequirementMet" class="text-deep-orange-darken-3 ms-1 font-weight-bold">
                                    {{ t('$vuetify.exactFlavorsRequired') }}
                                </span>
                            </template>
                            <template v-else>
                                {{ t('$vuetify.totalSelected') }}: {{ totalSelectedFlavorsCount }}
                            </template>
                        </span>
                    </div>
                    <v-chip v-if="maxFlavorsLimit && isFlavorRequirementMet" size="x-small" color="green" variant="flat" class="font-weight-bold text-white">
                        {{ t('$vuetify.completed') }}
                    </v-chip>
                    <v-chip v-else-if="maxFlavorsLimit && totalSelectedFlavorsCount >= maxFlavorsLimit" size="x-small" color="purple" variant="flat" class="font-weight-bold text-white">
                        {{ t('$vuetify.full') }}
                    </v-chip>
                </div>

                <!-- Flavors Selection List -->
                <div class="d-flex flex-column gap-2">
                    <div
                        v-for="(flavor, idx) in flavorSelections"
                        :key="'modal_f_' + idx"
                        class="pa-3 rounded-lg border d-flex align-center justify-space-between transition-all"
                        :class="flavor.qty > 0 ? 'bg-purple-lighten-5 border-purple' : 'bg-surface'"
                    >
                        <div>
                            <div class="font-weight-bold text-subtitle-2">{{ flavor.name }}</div>
                            <div v-if="flavor.price > 0" class="text-caption text-green font-weight-bold">
                                +{{ formatNumber(flavor.price) }}
                            </div>
                        </div>

                        <!-- Quantity Counter -->
                        <div class="d-flex align-center gap-2">
                            <v-btn
                                size="28"
                                icon="mdi-minus"
                                variant="tonal"
                                color="red"
                                :disabled="flavor.qty <= 0"
                                @click="decrementFlavor(flavor)"
                                :aria-label="t('$vuetify.decreaseQty')"
                            ></v-btn>
                            <span class="font-weight-bold px-2 text-subtitle-2" style="min-width: 24px; text-align: center;">
                                {{ flavor.qty }}
                            </span>
                            <v-btn
                                size="28"
                                icon="mdi-plus"
                                variant="elevated"
                                color="deep-purple-accent-3"
                                class="text-white"
                                :disabled="!canAddMoreFlavors"
                                @click="incrementFlavor(flavor)"
                                :aria-label="t('$vuetify.increaseQty')"
                            ></v-btn>
                        </div>
                    </div>
                </div>
            </v-card-text>

            <!-- Modal Footer -->
            <v-card-actions class="pa-4 border-t bg-surface d-flex align-center justify-space-between flex-wrap gap-2">
                <div>
                    <span class="text-caption text-medium-emphasis d-block">{{ t("$vuetify.totalPrice") }}</span>
                    <span class="text-subtitle-1 font-weight-bold text-green">
                        {{ formatNumber(currentModalTotalPrice) }}
                    </span>
                </div>
                <div class="d-flex gap-2">
                    <v-btn variant="tonal" color="grey-lighten-1" @click="flavorDialog = false" :aria-label="t('$vuetify.cancel')">
                        {{ t("$vuetify.cancel") }}
                    </v-btn>
                    <v-btn
                        color="deep-purple-accent-3"
                        variant="elevated"
                        class="px-5 font-weight-bold text-white"
                        prepend-icon="mdi-cart-plus"
                        :disabled="!isFlavorRequirementMet"
                        @click="confirmFlavorOrder"
                    >
                        {{ t("$vuetify.order") }}
                    </v-btn>
                </div>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <!-- Main Card -->
    <v-card hover class="pa-0 position-relative h-100 overflow-hidden d-flex flex-column"
        :class="isTwoColumns ? 'pb-1' : 'pb-2'" rounded="lg"
        @mouseenter="hoveredItemId = item.id" @mouseleave="hoveredItemId = null">
        
        <v-img cover :lazy-src="`/assets/images/${page.ku.logo}`" width="100%"
            class="mx-auto flex-shrink-0 item-card-img"
            :class="{ 'compact': isTwoColumns }"
            @error="item.image = page.ku.logo" :src="`/assets/images/${item.image}`"
            :alt="item.name[localeLang.myPrefix] || item.name.ku || item.name.en || item.name.ar || t('$vuetify.itemImage')"></v-img>
        
        <div class="w-100 text-center" :class="isTwoColumns ? 'mt-1 mb-0 px-1' : 'mt-2 mb-1 px-3'">
            <div :class="isTwoColumns ? 'text-body-2 font-weight-bold line-clamp-1' : 'font-weight-bold'"
                :title="item.name[localeLang.myPrefix] || item.name.ku">
                {{ item.name[localeLang.myPrefix] || item.name.ku }}
            </div>
            
            <!-- Flavor Badge on Card if item has flavors -->
            <div v-if="itemFlavors.length > 0" :class="isTwoColumns ? 'mt-0.5' : 'mt-1'">
                <v-chip :size="isTwoColumns ? 'x-small' : 'small'" color="purple" variant="tonal" prepend-icon="mdi-ice-cream"
                    :class="isTwoColumns ? 'px-1 text-caption' : ''">
                    {{ itemFlavors.length }} {{ t('$vuetify.flavors') }}
                    <span v-if="maxFlavorsLimit && !isTwoColumns" class="ms-1 font-weight-bold">
                        (تا {{ maxFlavorsLimit }} {{ t('$vuetify.balls') }})
                    </span>
                </v-chip>
            </div>

            <p v-if="itemDescription" class="text-caption text-grey-darken-1 px-1"
                :class="isTwoColumns ? 'line-clamp-2 mt-0.5 mb-0' : 'line-clamp-3 mt-1 mb-0'"
                :style="isTwoColumns ? 'font-size: 11px; line-height: 1.25;' : 'font-size: 12px; line-height: 1.3;'">
                {{ itemDescription }}
            </p>
        </div>

        <div class="mt-auto w-100">
            <!-- Multi Sizes Display -->
            <div v-if="allSizes.length > 1" class="d-flex flex-wrap justify-center align-center px-1 mb-1">
                <div v-for="size in allSizes" :key="'multi_' + size.id"
                    class="d-inline-block mx-1 rounded pt-1 position-relative text-center align-center mb-1">
                    <v-chip size="x-small" variant="elevated" color="pink"
                        style="position: absolute;left: -3px;top: -5px;z-index: 10;" v-if="getItemOrderCount(size.id) > 0">
                        {{ getItemOrderCount(size.id) }}
                    </v-chip>
                    <div class="d-block">
                        <div class="mb-1 d-inline-block bg-order-card rounded cursor-pointer"
                            :class="isTwoColumns ? 'py-1 px-1.5' : 'py-1 px-2'"
                            :style="isTwoColumns ? 'font-size: 12px' : 'font-size: 14px'"
                            @click="handleOrderClick(size)">
                            <p class="mb-0">
                                {{ size.size.name[localeLang.myPrefix] || size.size.name.ku }}
                            </p>
                            <p class="border-t mb-0 mt-1">
                                {{ formatNumber(size.price) }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Single Size Display -->
            <div v-else v-for="size in allSizes" :key="'single_' + size.id"
                class="mx-1 rounded pt-1 position-relative text-center align-center mb-1">
                <div class="w-100 align-center align-middle"
                    :class="isTwoColumns ? 'd-flex flex-column px-1' : 'd-flex justify-space-between px-2'">
                    <v-chip size="x-small" variant="elevated" color="pink"
                        style="position: absolute;left: -3px;top: -5px;z-index: 10;" v-if="getItemOrderCount(size.id) > 0">
                        {{ getItemOrderCount(size.id) }}
                    </v-chip>
                    <div class="font-weight-medium" :class="isTwoColumns ? 'text-caption font-weight-bold my-1' : ''">
                        <span v-if="!isTwoColumns">{{ t("$vuetify.price") }}: </span>
                        <span>{{ formatNumber(size.price) }}</span>
                    </div>

                    <!-- Counter if already ordered and NO flavors -->
                    <div :class="isTwoColumns ? 'd-flex justify-center align-center w-100 my-1' : 'float-end mb-1 mx-1'"
                        v-if="store.findOrder(size.id) && itemFlavors.length === 0 && getCookie('tableId')">
                        <v-btn :size="isTwoColumns ? '24' : '28'" :class="isTwoColumns ? 'px-2 me-1' : 'px-5 me-1'"
                            @click.stop="store.ToOrder(size, -1, item.name)"
                            color="red-darken-2" :aria-label="t('$vuetify.decreaseQty')">
                            <v-icon :size="isTwoColumns ? '16' : '20'">mdi-minus</v-icon>
                        </v-btn>
                        <v-btn :size="isTwoColumns ? '24' : '28'" :class="isTwoColumns ? 'px-2' : 'px-5'"
                            @click.stop="store.ToOrder(size, 1, item.name)" color="orderBtn"
                            :aria-label="t('$vuetify.increaseQty')">
                            <v-icon :size="isTwoColumns ? '16' : '20'">mdi-plus</v-icon>
                        </v-btn>
                    </div>

                    <!-- Order Button (Opens flavor modal if flavors exist) -->
                    <v-btn v-else-if="getCookie('tableId') && page.ku.active_order == 1"
                        :size="isTwoColumns ? 'x-small' : 'small'"
                        :block="isTwoColumns"
                        :class="isTwoColumns ? 'my-1 font-weight-bold w-100' : 'float-end mx-1 mb-1 font-weight-bold'"
                        @click="handleOrderClick(size)" color="orderBtn">
                        <v-icon class="me-1" :size="isTwoColumns ? '14' : '18'">mdi-plus</v-icon>
                        {{ itemFlavors.length > 0 ? t('$vuetify.selectFlavor') : t("$vuetify.order") }}
                    </v-btn>
                </div>
            </div>
        </div>

        <!-- Hover Multi-Size Overlay -->
        <v-sheet data-aos="fade-up" data-aos-duration="400" style="overflow-y: auto;z-index: 30;"
            v-if="hoveredItemId === item.id && allSizes.length > 1"
            class="position-absolute sizeBox bottom-0 left-0 right-0 pt-2 px-2" elevation="3">
            <div class="text-start pa-2 border rounded mb-1 d-flex justify-space-between flex-wrap align-center bg-surface"
                v-for="size in allSizes" :key="size.id">
                <div class="d-inline-block text-high-emphasis flex-grow-1" :style="isTwoColumns ? 'font-size: 12px; min-width: 80px;' : 'width: 140px;'">
                    {{ size.size.name[localeLang.myPrefix] || size.size.name.ku }}: <span class="text-en font-weight-bold">{{
                        formatNumber(size.price) }}</span>
                </div>
                
                <v-badge v-if="store.findOrder(size.id) && itemFlavors.length === 0 && getCookie('tableId')" color="pink"
                    :content="store.orders[size.id]?.qty || 1">
                    <v-btn :size="isTwoColumns ? 'x-small' : 'small'" class="me-1" @click="store.ToOrder(size, -1, item.name)" color="orderBtnMinus" :aria-label="t('$vuetify.decreaseQty')">
                        <v-icon :size="isTwoColumns ? '14' : '18'">mdi-minus</v-icon>
                    </v-btn>
                    <v-btn :size="isTwoColumns ? 'x-small' : 'small'" @click="store.ToOrder(size, 1, item.name)" color="orderBtnPlus" :aria-label="t('$vuetify.increaseQty')">
                        <v-icon :size="isTwoColumns ? '14' : '18'">mdi-plus</v-icon>
                    </v-btn>
                </v-badge>
                
                <v-btn :size="isTwoColumns ? 'x-small' : 'small'" v-else-if="getCookie('tableId')" @click="handleOrderClick(size)"
                    color="orderBtnPlus" class="font-weight-bold">
                    <v-icon class="me-1" :size="isTwoColumns ? '14' : '18'">mdi-plus</v-icon>
                    {{ itemFlavors.length > 0 ? t('$vuetify.selectFlavor') : t("$vuetify.order") }}
                </v-btn>
            </div>
        </v-sheet>

        <v-btn icon="mdi-heart" class="position-absolute" style="top: 2px;left: 2px;" @click="toggleHeart(item)"
            size="x-small" :color="isHeart(item.id) ? 'pink' : ''"
            :aria-label="isHeart(item.id) ? t('$vuetify.removeFromFavorites') : t('$vuetify.addToFavorites')">
        </v-btn>
    </v-card>
</template>

<style scoped>
.gap-2 {
    gap: 8px;
}
.cursor-pointer {
    cursor: pointer;
}
.line-clamp-1 {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
.line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
.sizeBox {
    background-color: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    border-radius: 10px 10px 0 0;
    max-height: 75%;
    z-index: 1000;
}
.item-card-img {
    height: 210px;
}
.item-card-img.compact {
    height: 135px;
}
@media (min-width: 960px) {
    .item-card-img,
    .item-card-img.compact {
        height: 195px !important;
    }
}
</style>
